/**
 * bodyParser.ts — client-side XML/JSON body parser for code_store row bodies.
 *
 * Parses the two canonical body shapes the dashboard needs:
 *
 * 1. option-bearing rows (msg-escalation, decision-card):
 *    <option n="1"><title>…</title><text>…</text><pros>…</pros><cons>…</cons></option>
 *    OR the legacy flat shape: <option n="A" label="…"><body>…</body></option>
 *
 * 2. action-card rows:
 *    <prescribed_action><step n="1">…</step>…</prescribed_action>
 *    OR the v2 shape: <action_steps><action_step n="1">…</action_step>…</action_steps>
 *
 * Per dispatch BOARDROOM-2026-05-05-071740-480 ac n=7: branching is on the
 * row's `type` field only; content is read AS-IS without inventing vocabulary.
 */

export type ParsedOption = {
  n: string;
  label: string;
  title: string;
  text: string;
  pros: string[];
  cons: string[];
};

export type ParsedStep = {
  n: number;
  body: string;
};

export type ParsedBody =
  | { kind: "options"; options: ParsedOption[] }
  | { kind: "steps"; steps: ParsedStep[] }
  | { kind: "empty" };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractText(xml: string, tag: string): string {
  const re = new RegExp(`<${tag}[^>]*>([\\s\\S]*?)</${tag}>`, "i");
  const m = re.exec(xml);
  return m ? m[1].trim() : "";
}

function extractList(xml: string, tag: string): string[] {
  const content = extractText(xml, tag);
  if (!content) return [];
  // Support both comma-separated and <item>…</item> lists.
  const itemRe = /<item[^>]*>([\s\S]*?)<\/item>/gi;
  const items: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = itemRe.exec(content)) !== null) {
    const t = m[1].trim();
    if (t) items.push(t);
  }
  if (items.length > 0) return items;
  // Fallback: return the whole block as a single item
  return content ? [content] : [];
}

// ---------------------------------------------------------------------------
// Option parser
// ---------------------------------------------------------------------------

/**
 * Parse option blocks from XML body.
 * Handles two shapes:
 *  - Rich:   <option n="1"><title>…</title><text>…</text><pros>…</pros><cons>…</cons></option>
 *  - Legacy: <option n="A" label="…"><body>…</body></option>
 */
export function parseOptionsFromXml(xmlBody: string): ParsedOption[] {
  if (!xmlBody) return [];
  const optionRe =
    /<option\s+n="([^"]+)"(?:\s+label="([^"]*)")?[^>]*>([\s\S]*?)<\/option>/gi;
  const options: ParsedOption[] = [];
  let m: RegExpExecArray | null;
  while ((m = optionRe.exec(xmlBody)) !== null) {
    const n = m[1].trim();
    const labelAttr = m[2]?.trim() ?? "";
    const inner = m[3] ?? "";

    const title = extractText(inner, "title") || labelAttr || n;
    const text = extractText(inner, "text") || extractText(inner, "body") || inner.trim();
    const pros = extractList(inner, "pros");
    const cons = extractList(inner, "cons");

    options.push({ n, label: labelAttr, title, text, pros, cons });
  }
  return options;
}

// ---------------------------------------------------------------------------
// Step parser
// ---------------------------------------------------------------------------

/**
 * Parse step blocks from XML body.
 * Handles:
 *  - v2: <prescribed_action><step n="1">…</step>…</prescribed_action>
 *  - v3: <action_steps><action_step n="1">…</action_step>…</action_steps>
 */
export function parseStepsFromXml(xmlBody: string): ParsedStep[] {
  if (!xmlBody) return [];
  // Try <step n="N"> first (prescribed_action shape)
  const stepRe = /<step\s+n="(\d+)"[^>]*>([\s\S]*?)<\/step>/gi;
  const steps: ParsedStep[] = [];
  let m: RegExpExecArray | null;
  while ((m = stepRe.exec(xmlBody)) !== null) {
    const text = m[2].trim();
    if (text) steps.push({ n: parseInt(m[1], 10), body: text });
  }
  if (steps.length > 0) return steps;

  // Try <action_step n="N"> (action_steps block shape)
  const actionStepRe = /<action_step\s+n="(\d+)"[^>]*>([\s\S]*?)<\/action_step>/gi;
  while ((m = actionStepRe.exec(xmlBody)) !== null) {
    const text = m[2].trim();
    if (text) steps.push({ n: parseInt(m[1], 10), body: text });
  }
  return steps;
}

// ---------------------------------------------------------------------------
// JSON option parser (fallback when body_json is available)
// ---------------------------------------------------------------------------

/**
 * Parse options from JSON body.
 * Writer-natural shape: { options: [{n, label, body, title, text, pros, cons}] }
 */
export function parseOptionsFromJson(bodyJson: unknown): ParsedOption[] {
  if (!bodyJson || typeof bodyJson !== "object") return [];
  const body = bodyJson as Record<string, unknown>;
  const raw = body["options"];
  if (!Array.isArray(raw)) return [];
  const options: ParsedOption[] = [];
  for (const opt of raw) {
    if (!opt || typeof opt !== "object") continue;
    const o = opt as Record<string, unknown>;
    const n = String(o["n"] ?? "");
    const label = String(o["label"] ?? "");
    const title = String(o["title"] ?? label ?? n);
    const text = String(o["text"] ?? o["body"] ?? "");
    const rawPros = o["pros"];
    const rawCons = o["cons"];
    const pros = Array.isArray(rawPros)
      ? rawPros.map(String)
      : typeof rawPros === "string" && rawPros
        ? [rawPros]
        : [];
    const cons = Array.isArray(rawCons)
      ? rawCons.map(String)
      : typeof rawCons === "string" && rawCons
        ? [rawCons]
        : [];
    if (n) options.push({ n, label, title, text, pros, cons });
  }
  return options;
}

// ---------------------------------------------------------------------------
// Top-level dispatcher
// ---------------------------------------------------------------------------

/**
 * Parse a row body into a structured shape, branching on row `type`.
 *
 * type=action-card  → steps
 * type=msg-escalation / decision-card / anything option-bearing → options
 *
 * Falls back gracefully: if body is absent, returns { kind: "empty" }.
 */
export function parseRowBody(
  type: string,
  bodyXml?: string | null,
  bodyJson?: unknown,
): ParsedBody {
  const isActionCard =
    type === "action-card" ||
    type === "msg-action" ||
    type === "action_card";

  if (isActionCard) {
    const steps = bodyXml ? parseStepsFromXml(bodyXml) : [];
    return { kind: "steps", steps };
  }

  // Option-bearing: msg-escalation, decision-card, and anything else
  // Try JSON body first (more structured), fall back to XML.
  if (bodyJson) {
    const opts = parseOptionsFromJson(bodyJson);
    if (opts.length > 0) return { kind: "options", options: opts };
  }
  if (bodyXml) {
    const opts = parseOptionsFromXml(bodyXml);
    if (opts.length > 0) return { kind: "options", options: opts };
  }

  return { kind: "empty" };
}
